/**
 * ADD POLISH MODAL - RESTRUCTURED RELEASE INFO!
 * 
 * Features:
 * - Correct Firebase path (users/{uid}/items)
 * - TagSelector for structured tags
 * - Multi-photo upload with captions & credits
 * - ✨ Auto image compression (saves ~70-90% storage!)
 * - Set default photo
 * - Category dropdown
 * - 🆕 Clean release type system with conditional fields
 * - Quick Tags with Lucide icons!
 * - List for Destash section with condition, price, swaps
 * 
 * Release Types:
 * - Core/Permanent, Collection, Event, Box, Group Custom,
 * - Exclusive, Charity, Limited Edition, Convention, Other
 */

import React, { useState, useEffect } from 'react';
import { X, Upload, Star, Trash2, Plus, Heart, Info, Store, Repeat, Eye, GripVertical, Pencil } from 'lucide-react';
import { SortablePhotoGrid } from '../common/SortablePhotoGrid';
import PhotoEditModal, { buildPhotoSummary } from '../common/PhotoEditModal';
import { AppIcons } from '../../constants/icons';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { getCurrentUser } from '../../firebase/auth';
import { buildDestashFields, clearDestashFields } from '../../services/destashDenormalization';
import { uploadPhotos as uploadPhotosService } from '../../services/photoUploadService';
import { itemDocKeys } from '../../utils/itemDocKeys';
import PhotoRightsAttestation from '../common/PhotoRightsAttestation';
import StorageUpgradeModal from '../StorageUpgradeModal';
import { CategoryColors, PriorityColors } from '../../constants/categoryColors';
import TagSelector from '../TagSelector';
import ConditionSelector from '../common/ConditionSelector';
import { ACCEPTED_IMAGE_TYPES } from '../../utils/imageUtils';
import PublicItemSearch from '../common/PublicItemSearch';
import type { SearchResult } from '../common/PublicItemSearch';
import ColorSampler from '../common/ColorSampler';
import BrandAutocomplete from '../common/BrandAutocomplete';
import CustomTagsEditor from '../common/CustomTagsEditor';
import { RequiredMark, RequiredLegend } from '../common';
import { useSellerCurrency } from '../../hooks/useSellerCurrency';
import { usePinnedCurrencies } from '../../hooks/usePinnedCurrencies';
import { getCurrencyInfo } from '../../constants/currencies';
import CurrencyPicker from '../common/CurrencyPicker';
import { normalizeTypography } from '../../utils/normalizeTypography';
import { getBrandBySlug, getBrandCollections } from '../../services/brandService';
import { slugify } from '../../utils/polishSlug';
import { useFirestoreBrands } from '../../services/useFirestoreBrands';
import type { BrandCategory } from '../../types/brandCategories';
import type {
  BaseType,
  Finish,
  HoloType,
  ColorShift,
  Particle,
  SpecialFeature,
  ColorFamily,
  Utility,
  ColorCharacter,
  CollectionItem
} from '../../types';

interface AddPolishModalProps {
  onClose: () => void;
  onSuccess?: (newItem: CollectionItem) => void;
  existingBrands?: string[]; // Brands already in user's collection for autocomplete
  userBrandCategories?: Record<string, BrandCategory>; // User's custom brand categorizations
}

// Pending photo (before upload)
interface PendingPhoto {
  id: string;
  file: File;
  preview: string;
  caption: string;
  creditName: string;
  creditUrl: string;
  isDefault: boolean;
  skinTone?: string;
  lighting?: string;
}

// Release type options
type ReleaseType = 'core' | 'collection' | 'event' | 'box' | 'groupCustom' | 'exclusive' | 'charity' | 'limitedEdition' | 'convention' | 'other' | 'unknown';

const RELEASE_TYPE_OPTIONS: { value: ReleaseType; label: string; emoji: string }[] = [
  { value: 'unknown', label: 'Unknown', emoji: '❓' },
  { value: 'core', label: 'Core / Permanent', emoji: '🏠' },
  { value: 'collection', label: 'Collection', emoji: '📚' },
  { value: 'event', label: 'Event', emoji: '🎪' },
  { value: 'box', label: 'Box', emoji: '📦' },
  { value: 'groupCustom', label: 'Group Custom', emoji: '👥' },
  { value: 'exclusive', label: 'Exclusive', emoji: '⭐' },
  { value: 'charity', label: 'Charity', emoji: '💝' },
  { value: 'limitedEdition', label: 'Limited Edition', emoji: '✨' },
  { value: 'convention', label: 'Convention', emoji: '🎫' },
  { value: 'other', label: 'Other', emoji: '📝' },
];

export default function AddPolishModal({ onClose, onSuccess, existingBrands = [], userBrandCategories = {} }: AddPolishModalProps) {
  // Canonicalized brands from the brands Firestore collection. Feeds the
  // BrandAutocomplete alongside the user's own history — picking one here
  // guarantees the "right" spelling and prevents new drift at the source.
  const firestoreBrands = useFirestoreBrands();
  // Basic fields
  const [brand, setBrand] = useState('');
  const [name, setName] = useState('');
  const [category, setCategory] = useState<string>('Nail Lacquer');
  const [colorDescription, setColorDescription] = useState('');
  const [scent, setScent] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [line, setLine] = useState('');
  const [swatchNumber, setSwatchNumber] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  // Per-item currency for THIS polish's purchase price (record-keeping).
  // Distinct from `currency` (seller's profile currency, used for destash
  // display to buyers). Falls back to seller currency at display time until
  // user picks a specific code.
  const [purchaseCurrency, setPurchaseCurrency] = useState<string>('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [storageLocation, setStorageLocation] = useState('');

  // ============================================
  // NEW RELEASE INFO STRUCTURE
  // ============================================
  const [releaseType, setReleaseType] = useState<ReleaseType>('unknown');
  
  // Conditional fields based on releaseType
  const [collectionName, setCollectionName] = useState('');       // for 'collection'
  /** Brand's official collection names, fetched when the user picks a
   *  brand. Used as `<datalist>` suggestions on the collection-name
   *  input so collectors type-through toward the canonical spelling
   *  rather than inventing variants. Empty when the brand hasn't
   *  defined any collections or isn't a verified brand. */
  const [brandCollectionSuggestions, setBrandCollectionSuggestions] = useState<string[]>([]);

  // Fetch the brand's official collections whenever the brand string
  // changes. Debounced by React batching so a fast typist doesn't spam
  // Firestore; the fetch runs against `brands` by slug and returns
  // empty for unknown or unclaimed brands.
  useEffect(() => {
    const b = brand.trim();
    if (!b) {
      setBrandCollectionSuggestions([]);
      return;
    }
    let cancelled = false;
    const brandSlug = slugify(b);
    (async () => {
      try {
        const brandDoc = await getBrandBySlug(brandSlug);
        if (cancelled || !brandDoc) {
          if (!cancelled) setBrandCollectionSuggestions([]);
          return;
        }
        const cols = await getBrandCollections(brandDoc.id);
        if (!cancelled) {
          setBrandCollectionSuggestions(cols.map((c) => c.name));
        }
      } catch {
        if (!cancelled) setBrandCollectionSuggestions([]);
      }
    })();
    return () => { cancelled = true; };
  }, [brand]);

  const [eventName, setEventName] = useState('');                 // for 'event' (was eventSource)
  const [eventTheme, setEventTheme] = useState('');               // for 'event'
  const [boxName, setBoxName] = useState('');                     // for 'box'
  const [boxTheme, setBoxTheme] = useState('');                   // for 'box'
  const [groupName, setGroupName] = useState('');                 // for 'groupCustom'
  const [groupTheme, setGroupTheme] = useState('');               // for 'groupCustom'
  const [exclusiveTo, setExclusiveTo] = useState('');             // for 'exclusive'
  const [exclusiveTheme, setExclusiveTheme] = useState('');       // for 'exclusive' - theme
  const [charityName, setCharityName] = useState('');             // for 'charity'
  const [charityCause, setCharityCause] = useState('');           // for 'charity' - the cause
  const [conventionName, setConventionName] = useState('');       // for 'convention'
  
  // Always available fields
  const [collabWith, setCollabWith] = useState('');               // Who the collab is with
  const [collabTheme, setCollabTheme] = useState('');             // Collab theme
  const [releaseYear, setReleaseYear] = useState('');
  const [releaseSeason, setReleaseSeason] = useState('');
  const [releaseMonth, setReleaseMonth] = useState('');
  
  // Re-release tracking
  const [isRerelease, setIsRerelease] = useState(false);
  const [originalReleaseYear, setOriginalReleaseYear] = useState('');
  const [originalReleaseSeason, setOriginalReleaseSeason] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  
  // Duo/Trio/Set tracking
  const [setNameField, setSetNameField] = useState('');  // e.g., "Autumn Duo"
  const [setType, setSetType] = useState<'duo' | 'trio' | 'quad' | 'set' | ''>('');  // duo, trio, quad, set

  // Structured tags
  const [baseType, setBaseType] = useState<BaseType | undefined>(undefined);
  const [finish, setFinish] = useState<Finish[]>([]);
  const [holoType, setHoloType] = useState<HoloType[]>([]);
  const [colorShift, setColorShift] = useState<ColorShift | undefined>(undefined);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [specialFeatures, setSpecialFeatures] = useState<SpecialFeature[]>([]);
  const [colorFamily, setColorFamily] = useState<ColorFamily[]>([]);
  const [utility, setUtility] = useState<Utility[]>([]);
  const [colorCharacter, setColorCharacter] = useState<ColorCharacter[]>([]);

  /** Freeform user labels stored on `tags[]`. Distinct from the
   *  structured Tag categories above — this is where things like
   *  "gift", "birthday polish", "wedding manicure", or imported
   *  Airtable tags land. Managed via the shared CustomTagsEditor. */
  const [customTags, setCustomTags] = useState<string[]>([]);

  // Sampled colors (from ColorSampler)
  const [sampledColors, setSampledColors] = useState<string[]>([]);
  
  // Notes
  const [publicNotes, setPublicNotes] = useState('');
  const [privateNotes, setPrivateNotes] = useState('');
  
  // Track if form was pre-filled from search
  const [wasPreFilled, setWasPreFilled] = useState(false);
  // When a search result carries a photoUrl (from another collector's
  // item), attach it here so the resulting polish shows their photo. Users
  // can dismiss it if they want to shoot their own instead. Kept separate
  // from `pendingPhotos` because those hold File uploads that go through
  // Storage; this one references an existing URL directly (community
  // source, DMCA-tracked at write time).
  const [attachedPhotoFromSearch, setAttachedPhotoFromSearch] = useState<string | null>(null);

  // Quick Tag toggles (personal collection flags)
  const [onWishlist, setOnWishlist] = useState(false);
  const [isoPriority, setIsoPriority] = useState<'iso' | 'diso' | 'unicorn'>('iso');
  const [isFavorite, setIsFavorite] = useState(false);
  const [isRareLE, setIsRareLE] = useState(false);
  const [needsInfo, setNeedsInfo] = useState(false);
  // Private polish — hides from every public surface (public profile,
  // brand pages, community aggregations, Discover). Marketplace is
  // unaffected: if user also toggles For Destash, item still appears on
  // Marketplace (with a warning near the toggle explaining that).
  const [isPrivate, setIsPrivate] = useState(false);
  const [swatched, setSwatched] = useState(false);

  // Seller's chosen currency from profile. Drives the symbol shown in the
  // price inputs so a EUR seller doesn't see "$" next to their price. The
  // save side (buildDestashFields) reads the same profile field, so what
  // the seller sees here matches what buyers see on Marketplace.
  const currency = useSellerCurrency();
  const { pinnedCurrencies, setPinnedCurrencies } = usePinnedCurrencies();
  // Symbol driving the Purchase Price prefix — user's per-item override
  // when set, else the seller's profile currency. Destash Price stays
  // locked to profile currency (buyer-facing) and uses `currency.symbol`.
  const purchaseSymbol = purchaseCurrency
    ? getCurrencyInfo(purchaseCurrency).symbol
    : currency.symbol;
  const purchaseSymbolPad =
    purchaseSymbol.length <= 1 ? 'pl-7' : purchaseSymbol.length <= 2 ? 'pl-10' : 'pl-12';
  const symbolPad =
    currency.symbol.length <= 1 ? 'pl-7' : currency.symbol.length <= 2 ? 'pl-10' : 'pl-12';

  // Marketplace / Destash listing state
  const [isForDestash, setIsForDestash] = useState(false);
  const [destashCondition, setDestashCondition] = useState('');
  const [destashPrice, setDestashPrice] = useState('');
  const [isOpenToSwaps, setIsOpenToSwaps] = useState(false);
  const [destashNotes, setDestashNotes] = useState('');
  const [destashQuantityAvailable, setDestashQuantityAvailable] = useState(1);
  const [showListedSuccess, setShowListedSuccess] = useState(false);

  // Photos
  const [pendingPhotos, setPendingPhotos] = useState<PendingPhoto[]>([]);
  // Which pending photo is open in the per-photo edit modal, if any.
  // Editing collapses the inline 5-input stack down to a one-line blurb
  // and hoists the form into a modal with the photo dominating — matches
  // the mobile PhotoDetailsModal pattern the tester asked for.
  const [editingPhotoId, setEditingPhotoId] = useState<string | null>(null);

  // Rights attestation — required per-submission when the user is attaching
  // NEW photos. Users who take/own their photos check it; DMCA reports have
  // a per-upload timestamped record to fall back on. No prompt when the
  // form has no new photos (edit-name-only saves are frictionless).
  const [photoRightsConfirmed, setPhotoRightsConfirmed] = useState(false);

  // Form state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showStorageUpgradeModal, setShowStorageUpgradeModal] = useState(false);

  // Handle tag changes from TagSelector
  const handleTagChange = (categoryKey: string, value: any) => {
    switch (categoryKey) {
      case 'baseType': setBaseType(value); break;
      case 'finish': setFinish(value); break;
      case 'holoType': setHoloType(value); break;
      case 'colorShift': setColorShift(value); break;
      case 'particles': setParticles(value); break;
      case 'specialFeatures': setSpecialFeatures(value); break;
      case 'colorFamily': setColorFamily(value); break;
      case 'utility': setUtility(value); break;
      case 'colorCharacter': setColorCharacter(value); break;
    }
  };

  // Handle selection from public search results
  const handleSearchSelect = (result: SearchResult) => {
    // Pre-fill basic info
    setBrand(result.brand);
    setName(result.name);
    if (result.category) setCategory(result.category);
    if (result.colorDescription) setColorDescription(result.colorDescription);
    if (result.line) setLine(result.line);
    if (result.collectionName) {
      setCollectionName(result.collectionName);
      setReleaseType('collection');
    }
    
    // Pre-fill tags
    if (result.baseType) setBaseType(result.baseType as BaseType);
    if (result.finish) setFinish(result.finish as Finish[]);
    if (result.holoType) setHoloType(result.holoType as HoloType[]);
    if (result.colorShift) setColorShift(result.colorShift as ColorShift);
    if (result.particles) setParticles(result.particles as Particle[]);
    if (result.specialFeatures) setSpecialFeatures(result.specialFeatures as SpecialFeature[]);
    if (result.colorFamily) setColorFamily(result.colorFamily as ColorFamily[]);
    if (result.utility) setUtility(result.utility as Utility[]);
    if (result.colorCharacter) setColorCharacter(result.colorCharacter as ColorCharacter[]);
    
    // Pre-fill colors
    if (result.colors && result.colors.length > 0) {
      setSampledColors(result.colors);
    }

    // Attach the search result's photo (if any). Users used to see the
    // rest of the fields populate but end up with a photo-less polish
    // when using this flow — mismatched behavior vs. tapping "Add" on a
    // discovery tile, which does include the photo.
    if (result.photoUrl) {
      setAttachedPhotoFromSearch(result.photoUrl);
    }

    setWasPreFilled(true);
  };

  // Handle file selection (multiple)
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newPhotos: PendingPhoto[] = [];

    Array.from(files).forEach((file, index) => {
      // Create preview URL
      const preview = URL.createObjectURL(file);

      // Extract caption from filename
      const nameWithoutExt = file.name.replace(/\.(jpg|jpeg|png|gif|webp)$/i, '');
      const parts = nameWithoutExt.split(' - ');
      const caption = parts.length >= 2 ? parts.slice(1).join(' - ').trim() : '';

      newPhotos.push({
        id: `${Date.now()}-${index}`,
        file,
        preview,
        caption,
        creditName: '',
        creditUrl: '',
        isDefault: pendingPhotos.length === 0 && index === 0,
      });
    });

    setPendingPhotos((prev) => [...prev, ...newPhotos]);
    e.target.value = '';
  };

  // Remove a pending photo
  const removePhoto = (id: string) => {
    setPendingPhotos((prev) => {
      const updated = prev.filter((p) => p.id !== id);
      // If we removed the default, make the first one default
      if (updated.length > 0 && !updated.some((p) => p.isDefault)) {
        updated[0].isDefault = true;
      }
      return updated;
    });
  };

  // Set a photo as default
  const setDefaultPhoto = (id: string) => {
    setPendingPhotos((prev) =>
      prev.map((p) => ({
        ...p,
        isDefault: p.id === id,
      }))
    );
  };

  // Update photo caption
  const updatePhotoCaption = (id: string, caption: string) => {
    setPendingPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, caption } : p))
    );
  };

  // Update photo credit
  const updatePhotoCredit = (id: string, field: 'creditName' | 'creditUrl', value: string) => {
    setPendingPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  /** Update an optional per-photo context field (skinTone / lighting).
   *  Same shape as updatePhotoCaption but keyed generically so we can
   *  reuse for other optional string fields without another handler. */
  const updatePhotoContext = (id: string, field: 'skinTone' | 'lighting', value: string) => {
    setPendingPhotos((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
    );
  };

  // Upload photos to Firebase Storage
  const uploadPhotos = async (userId: string) => {
    if (pendingPhotos.length === 0) return { photos: [], blocked: false };

    const result = await uploadPhotosService(
      userId,
      pendingPhotos,
      'items',
      (progress: number) => setUploadProgress(progress),
      undefined,
      // Every pending photo shares the form-level attestation the user
      // just confirmed. The submit gate above guarantees this is true
      // when photos exist, so we pass the state value straight through.
      photoRightsConfirmed
    );

    if (result.storageBlocked) {
      setShowStorageUpgradeModal(true);
      return { photos: [], blocked: true };
    }

    if (result.errors.length > 0) {
      console.warn('Some photos failed to upload:', result.errors);
    }

    return { photos: result.photos, blocked: false };
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const currentUser = getCurrentUser();
    if (!currentUser) {
      alert('Please sign in to add a polish');
      return;
    }

    // Validate destash fields if listing
    if (isForDestash) {
      if (!destashCondition) {
        alert('Please select a condition to list on the marketplace');
        return;
      }
      if (!destashPrice) {
        alert('Please enter a price to list on the marketplace');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Upload photos first
      let photos = [] as any[];
      if (pendingPhotos.length > 0) {
        const uploadResult = await uploadPhotos(currentUser.uid);

        // If storage was blocked, stop here — the upgrade modal is showing
        if (uploadResult.blocked) {
          setIsSubmitting(false);
          return;
        }

        photos = uploadResult.photos;
      }

      // If a search-result photo was attached, add it to the photos array.
      // Reference the URL directly (no copy to user storage), tag it
      // community-sourced for DMCA compliance. Made default only when the
      // user didn't upload their own photos — their upload wins otherwise.
      if (attachedPhotoFromSearch) {
        photos.push({
          url: attachedPhotoFromSearch,
          isDefault: photos.length === 0,
          uploadedBy: currentUser.uid,
          uploadedAt: new Date().toISOString(),
          source: 'community',
          sourceUrl: attachedPhotoFromSearch,
          dmcaStatus: 'active',
        });
      }

      // Normalize smart-quote / typographic-dash variants down to plain
      // ASCII so an iOS user typing "Lil’ Sweet" writes the same string
      // as an Android user typing "Lil' Sweet". Prevents typography-only
      // duplicates from ever entering Firestore. Both `name` and `brand`
      // flow into itemDocKeys() too, so the derived slugs stay consistent.
      const normalizedBrand = normalizeTypography(brand.trim());
      const normalizedName = normalizeTypography(name.trim());

      // Build the polish document with new release structure
      const polishData: any = {
        // Required fields
        itemType: 'polish',
        userId: currentUser.uid,
        brand: normalizedBrand,
        name: normalizedName,
        // Derived key fields — brand/name lower + slug forms. Required for
        // aggregation queries (community item aggregation on non-polish
        // types, brand-page grouping). Uses the shared helper so a future
        // field addition only touches one place.
        ...itemDocKeys(normalizedBrand, normalizedName),
        category,
        quantity,
        dateAdded: Timestamp.now(),

        // Optional text fields (only include if not empty)
        ...(colorDescription.trim() && { colorDescription: colorDescription.trim() }),
        ...(scent.trim() && { scent: scent.trim() }),
        ...(line.trim() && { line: line.trim() }),
        ...(swatchNumber.trim() && { swatchNumber: swatchNumber.trim() }),
        
        // ============================================
        // NEW RELEASE INFO STRUCTURE
        // ============================================
        releaseType,
        
        // Conditional fields based on releaseType
        ...(releaseType === 'collection' && collectionName.trim() && { collectionName: collectionName.trim() }),
        ...(releaseType === 'event' && eventName.trim() && { eventName: eventName.trim() }),
        ...(releaseType === 'event' && eventTheme.trim() && { eventTheme: eventTheme.trim() }),
        ...(releaseType === 'box' && boxName.trim() && { boxName: boxName.trim() }),
        ...(releaseType === 'box' && boxTheme.trim() && { boxTheme: boxTheme.trim() }),
        ...(releaseType === 'groupCustom' && groupName.trim() && { groupName: groupName.trim() }),
        ...(releaseType === 'groupCustom' && groupTheme.trim() && { groupTheme: groupTheme.trim() }),
        ...(releaseType === 'exclusive' && exclusiveTo.trim() && { exclusiveTo: exclusiveTo.trim() }),
        ...(releaseType === 'exclusive' && exclusiveTheme.trim() && { exclusiveTheme: exclusiveTheme.trim() }),
        ...(releaseType === 'charity' && charityName.trim() && { charityName: charityName.trim() }),
        ...(releaseType === 'charity' && charityCause.trim() && { charityCause: charityCause.trim() }),
        ...(releaseType === 'convention' && conventionName.trim() && { conventionName: conventionName.trim() }),
        
        // Always available fields
        ...(collabWith.trim() && { collabWith: collabWith.trim() }),
        ...(collabTheme.trim() && { collabTheme: collabTheme.trim() }),
        ...(releaseYear && { releaseYear: parseInt(releaseYear) }),
        ...(releaseSeason && { releaseSeason }),
        ...(releaseMonth && { releaseMonth: parseInt(releaseMonth) }),
        
        // Re-release tracking
        ...(isRerelease && { isRerelease: true }),
        ...(isRerelease && originalReleaseYear && { originalReleaseYear: parseInt(originalReleaseYear) }),
        ...(isRerelease && originalReleaseSeason && { originalReleaseSeason }),
        ...(releaseNotes.trim() && { releaseNotes: releaseNotes.trim() }),
        
        // Duo/Trio/Set tracking
        ...(setType && { setType }),
        ...(setType && setNameField.trim() && { setName: setNameField.trim() }),

        // Structured tags (only include if set)
        ...(baseType && { baseType }),
        ...(finish.length > 0 && { finish }),
        ...(holoType.length > 0 && { holoType }),
        ...(colorShift && { colorShift }),
        ...(particles.length > 0 && { particles }),
        ...(specialFeatures.length > 0 && { specialFeatures }),
        ...(colorFamily.length > 0 && { colorFamily }),
        ...(utility.length > 0 && { utility }),
        ...(colorCharacter.length > 0 && { colorCharacter }),
        // Freeform tags[] captured via CustomTagsEditor. Persisted as
        // Firestore array (never as an empty array — omitted when
        // nothing was entered to avoid a bogus [] on the doc).
        ...(customTags.length > 0 && { tags: customTags }),

        // Sampled colors (from ColorSampler)
        ...(sampledColors.length > 0 && { 
          colors: sampledColors,
          primaryColor: sampledColors[0]
        }),

        // Notes
        ...(publicNotes.trim() && { publicNotes: publicNotes.trim() }),
        ...(privateNotes.trim() && { notes: privateNotes.trim() }),

        // Price & Storage
        ...(purchasePrice && { price: parseFloat(purchasePrice) }),
        ...(purchasePrice && purchaseCurrency && { priceCurrency: purchaseCurrency }),
        ...(purchaseDate && { purchaseDate: new Date(purchaseDate) }),
        ...(storageLocation.trim() && { storage: storageLocation.trim() }),

        // Photos (only include if we have any)
        ...(photos.length > 0 && { photos }),

        // Quick Tag toggles (personal collection flags)
        onWishlist,
        ...(onWishlist && { isoPriority }),
        isFavorite,
        isRareLE,
        needsInfo,
        ...(isPrivate && { isPrivate: true }),
        swatched,

        // Destash/Marketplace fields
        isForDestash,
        ...(isForDestash && {
          destashCondition,
          destashPrice: parseFloat(destashPrice),
          destashQuantityAvailable,
          isOpenToSwaps,
          destashStatus: 'available',
          destashDate: new Date().toISOString(),
          ...(destashNotes.trim() && { destashNotes: destashNotes.trim() }),
        }),
        // Denormalized seller currency + ships-to snapshot so Marketplace
        // queries can filter without joining to the user doc. Only read
        // the profile when actually listing; clear the fields explicitly
        // otherwise so no stale values linger from a prior listing.
        ...(isForDestash
          ? await buildDestashFields(currentUser.uid)
          : clearDestashFields()),

        // Default values
        hasSwatched: photos.length > 0,
        isForSale: false,
        favorited: false,
        usedInProjects: [],
        timesUsed: 0
      };

      // Save to Firestore
      const docRef = await addDoc(collection(db, 'users', currentUser.uid, 'items'), polishData);

      // Build the complete item with the new ID for local state update
      const newItem: CollectionItem = {
        id: docRef.id,
        ...polishData,
        // Convert Timestamp to ISO string for local state
        dateAdded: new Date().toISOString(),
      } as CollectionItem;

      // Clean up preview URLs
      pendingPhotos.forEach(p => URL.revokeObjectURL(p.preview));

      // Show success for destash listings
      if (isForDestash) {
        setShowListedSuccess(true);
        setTimeout(() => {
          if (onSuccess) onSuccess(newItem);
          onClose();
        }, 1500);
      } else {
        if (onSuccess) onSuccess(newItem);
        onClose();
      }
    } catch (error: any) {
      console.error('Error adding polish:', error);
      if (!error.message?.includes('storage limit')) {
        alert('Failed to add polish. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper to render conditional release fields
  const renderReleaseFields = () => {
    switch (releaseType) {
      case 'collection':
        return (
          <div className="mt-3 p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
            <div>
              <label className="block text-sm font-medium text-purple-700 mb-1">
                📚 Collection Name
              </label>
              <input
                type="text"
                value={collectionName}
                onChange={(e) => setCollectionName(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., Enchanted Forest, Winter Wonderland"
                list={brandCollectionSuggestions.length > 0 ? 'brand-collection-suggestions' : undefined}
              />
              {brandCollectionSuggestions.length > 0 && (
                <>
                  {/* Datalist reflects the brand's official collections.
                      Users typing "refl" get "Reflections" as a
                      suggestion — reduces variant drift at input time. */}
                  <datalist id="brand-collection-suggestions">
                    {brandCollectionSuggestions.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                  <div className="text-[10px] text-purple-600 mt-1 font-body">
                    Suggestions from {brand}'s official collections.
                  </div>
                </>
              )}
            </div>
          </div>
        );
      
      case 'event':
        return (
          <div className="mt-3 p-3 bg-pink-50 border border-pink-200 rounded-lg space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-pink-700 mb-1">
                  🎪 Event Name
                </label>
                <input
                  type="text"
                  value={eventName}
                  onChange={(e) => setEventName(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Polish Pickup, HHC"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-pink-700 mb-1">
                  🎨 Event Theme
                </label>
                <input
                  type="text"
                  value={eventTheme}
                  onChange={(e) => setEventTheme(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Periodic Table, Autumn Harvest"
                />
              </div>
            </div>
          </div>
        );
      
      case 'box':
        return (
          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">
                  📦 Box Name
                </label>
                <input
                  type="text"
                  value={boxName}
                  onChange={(e) => setBoxName(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Polished Gamers Box, Holo Hookup"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">
                  🎨 Box Theme
                </label>
                <input
                  type="text"
                  value={boxTheme}
                  onChange={(e) => setBoxTheme(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., October 2024, Cozy Season"
                />
              </div>
            </div>
          </div>
        );
      
      case 'groupCustom':
        return (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  👥 Group Name
                </label>
                <input
                  type="text"
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Reddit Lacqueristas, Polish Addicts FB"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-1">
                  🎨 Theme <span className="font-normal text-blue-500">(optional)</span>
                </label>
                <input
                  type="text"
                  value={groupTheme}
                  onChange={(e) => setGroupTheme(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Anniversary Custom, Holiday 2024"
                />
              </div>
            </div>
          </div>
        );
      
      case 'exclusive':
        return (
          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg space-y-3">
            <div>
              <label className="block text-sm font-medium text-yellow-700 mb-1">
                ⭐ Exclusive To
              </label>
              <input
                type="text"
                value={exclusiveTo}
                onChange={(e) => setExclusiveTo(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., Color4Nails, Holo Taco website"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-yellow-700 mb-1">
                🏷️ Exclusive Theme <span className="font-normal text-yellow-500">(optional)</span>
              </label>
              <input
                type="text"
                value={exclusiveTheme}
                onChange={(e) => setExclusiveTheme(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., Black Friday 2024, Launch Exclusive"
              />
            </div>
          </div>
        );
      
      case 'charity':
        return (
          <div className="mt-3 p-3 bg-rose-50 border border-rose-200 rounded-lg space-y-3">
            <div>
              <label className="block text-sm font-medium text-rose-700 mb-1">
                💝 Charity Name
              </label>
              <input
                type="text"
                value={charityName}
                onChange={(e) => setCharityName(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., Autism Speaks, Red Cross"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-rose-700 mb-1">
                🎗️ Cause <span className="font-normal text-rose-500">(optional)</span>
              </label>
              <input
                type="text"
                value={charityCause}
                onChange={(e) => setCharityCause(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., Autism Awareness, Disaster Relief, Cancer Research"
              />
            </div>
          </div>
        );
      
      case 'convention':
        return (
          <div className="mt-3 p-3 bg-teal-50 border border-teal-200 rounded-lg">
            <label className="block text-sm font-medium text-teal-700 mb-1">
              🎫 Convention Name
            </label>
            <input
              type="text"
              value={conventionName}
              onChange={(e) => setConventionName(e.target.value)}
              className="input-light font-body"
              placeholder="e.g., Polish Con, Indie Expo"
            />
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-light max-w-2xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="modal-header-light">
          <h2 className="text-xl font-bold text-light-primary font-body">Add Polish</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-default"
          >
            <X className="icon-md" />
          </button>
        </div>

        {/* Success Message for Destash */}
        {showListedSuccess ? (
          <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <Store className="w-10 h-10 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-green-700 mb-2">Listed!</h3>
            <p className="text-gray-600">Your polish is now on the marketplace.</p>
          </div>
        ) : (
        <>
        {/* Scrollable Content */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-6">
          {/* Public Item Search */}
          <PublicItemSearch
            itemType="polish"
            onSelect={handleSearchSelect}
          />
          
          {wasPreFilled && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              ✓ Pre-filled from community data! Review and adjust as needed.
            </div>
          )}

          {attachedPhotoFromSearch && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-3">
              <img
                src={attachedPhotoFromSearch}
                alt="Attached community photo"
                className="w-16 h-16 rounded-lg object-cover flex-shrink-0"
              />
              <div className="flex-1">
                <p className="text-blue-800 text-sm font-medium">Community photo attached</p>
                <p className="text-blue-700 text-xs mt-0.5">
                  From another collector's item. You can upload your own below to use as the default instead.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setAttachedPhotoFromSearch(null)}
                className="text-blue-600 hover:text-red-500 text-xs font-medium underline flex-shrink-0"
              >
                Remove
              </button>
            </div>
          )}

          {/* === PHOTOS SECTION === */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">
              📸 Photos
            </h3>

            {/* Photo Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
              {pendingPhotos.length === 0 ? (
                <label className="flex flex-col items-center justify-center cursor-pointer py-8">
                  <Upload className="w-12 h-12 text-gray-400 mb-2" />
                  <span className="text-gray-500 font-body">Click to upload photos</span>
                  <span className="text-xs text-gray-400 mt-1">(Auto-compressed to save storage)</span>
                  <input
                    type="file"
                    accept={ACCEPTED_IMAGE_TYPES}
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              ) : (
                <div className="space-y-4">
                  {/* Photo Grid — drag any tile by the ⋮⋮ handle in its
                      top-right to reorder. Drop position becomes the new
                      display order in the detail carousel. */}
                  <SortablePhotoGrid
                    items={pendingPhotos}
                    onReorder={setPendingPhotos}
                    className="grid grid-cols-2 md:grid-cols-3 gap-4"
                  >
                    {(photo, _index, dragHandleProps) => (
                      <div
                        className={`relative border-2 rounded-lg overflow-hidden ${
                          photo.isDefault ? 'border-yellow-400 ring-2 ring-yellow-200' : 'border-gray-200'
                        }`}
                      >
                        <img
                          src={photo.preview}
                          alt="Preview"
                          className="w-full aspect-square object-cover"
                        />
                        {/* Default badge */}
                        {photo.isDefault && (
                          <div className="absolute top-1 left-1 bg-yellow-400 text-yellow-900 text-xs px-2 py-0.5 rounded-full font-bold">
                            Default
                          </div>
                        )}
                        {/* Action buttons */}
                        <div className="absolute top-1 right-1 flex gap-1">
                          {/* Drag handle — the only surface that starts a
                              reorder drag. Grip icon signals intent. */}
                          <button
                            type="button"
                            {...dragHandleProps}
                            className="p-1 bg-white/90 rounded-full cursor-grab active:cursor-grabbing touch-none"
                            title="Drag to reorder"
                            aria-label="Drag to reorder"
                          >
                            <GripVertical className="w-4 h-4 text-gray-600" />
                          </button>
                          {!photo.isDefault && (
                            <button
                              type="button"
                              onClick={() => setDefaultPhoto(photo.id)}
                              className="p-1 bg-white/90 rounded-full hover:bg-yellow-100"
                              title="Set as default"
                            >
                              <Star className="w-4 h-4 text-gray-600" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removePhoto(photo.id)}
                            className="p-1 bg-white/90 rounded-full hover:bg-red-100"
                            title="Remove"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                        {/* Compact summary + Edit trigger. Replaces the
                            5-input stack that used to double the tile
                            height per photo. */}
                        <div className="p-2 bg-white flex items-start gap-2">
                          <div className="flex-1 min-w-0 text-xs text-gray-700">
                            {buildPhotoSummary(photo) || (
                              <span className="text-gray-400 italic">
                                No details yet.
                              </span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setEditingPhotoId(photo.id)}
                            className="flex-shrink-0 px-2 py-1 text-xs bg-purple-100 text-purple-700 rounded hover:bg-purple-200 flex items-center gap-1"
                            title="Edit caption, credit, skin tone, lighting"
                          >
                            <Pencil className="w-3 h-3" />
                            Edit
                          </button>
                        </div>
                      </div>
                    )}
                  </SortablePhotoGrid>
                  <PhotoEditModal
                    open={editingPhotoId !== null}
                    photo={(() => {
                      const p = pendingPhotos.find((x) => x.id === editingPhotoId);
                      if (!p) return null;
                      return {
                        imageUri: p.preview,
                        caption: p.caption,
                        creditName: p.creditName,
                        creditUrl: p.creditUrl,
                        skinTone: p.skinTone,
                        lighting: p.lighting,
                      };
                    })()}
                    onClose={() => setEditingPhotoId(null)}
                    onSave={(patch) => {
                      if (!editingPhotoId) return;
                      updatePhotoCaption(editingPhotoId, patch.caption);
                      updatePhotoCredit(editingPhotoId, 'creditName', patch.creditName);
                      updatePhotoCredit(editingPhotoId, 'creditUrl', patch.creditUrl);
                      updatePhotoContext(editingPhotoId, 'skinTone', patch.skinTone);
                      updatePhotoContext(editingPhotoId, 'lighting', patch.lighting);
                    }}
                  />
                  {/* Add More Button — kept outside the sortable grid so
                      dnd-kit doesn't try to reorder the upload tile. */}
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 aspect-square max-w-[200px]">
                    <Plus className="w-8 h-8 text-gray-400" />
                    <span className="text-xs text-gray-400 mt-1">Add More</span>
                    <input
                      type="file"
                      accept={ACCEPTED_IMAGE_TYPES}
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>
              )}
            </div>
            <PhotoRightsAttestation
              pendingCount={pendingPhotos.length}
              confirmed={photoRightsConfirmed}
              onChange={setPhotoRightsConfirmed}
            />
          </div>

          {/* === COLOR SAMPLER (shows when photos exist) === */}
          {pendingPhotos.length > 0 && pendingPhotos.some(p => p.isDefault) && (
            <div className="space-y-4">
              <h3 className="section-header-light font-body">
                🎨 Sample Colors
              </h3>
              <ColorSampler
                photoUrl={pendingPhotos.find(p => p.isDefault)?.preview || pendingPhotos[0].preview}
                colors={sampledColors}
                onChange={setSampledColors}
              />
            </div>
          )}

          {/* === BASIC INFO === */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">
              ✨ Basic Info
            </h3>
            <RequiredLegend />

            {/* Brand & Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Brand <RequiredMark />
                </label>
                <BrandAutocomplete
                  value={brand}
                  onChange={setBrand}
                  existingBrands={existingBrands}
                  userBrandCategories={userBrandCategories}
                  firestoreBrands={firestoreBrands}
                  placeholder="e.g., ILNP"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Name <RequiredMark />
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Mega (S)"
                />
              </div>
            </div>

            {/* Line */}
            <div>
              <label className="block text-sm font-medium text-light-secondary mb-1">
                Line
              </label>
              <input
                type="text"
                value={line}
                onChange={(e) => setLine(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., Ultra Chrome, Juicy Collection"
              />
            </div>

            {/* Category & Quantity Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Category
                </label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="select-light font-body"
                >
                  <option value="Base Coat">Base Coat</option>
                  <option value="Builder Gel">Builder Gel</option>
                  <option value="Dip Powder">Dip Powder</option>
                  <option value="Gel Polish">Gel Polish</option>
                  <option value="Gel Stamping Polish">Gel Stamping Polish</option>
                  <option value="Nail Lacquer">Nail Lacquer</option>
                  <option value="Poly Gel">Poly Gel</option>
                  <option value="Stamping Polish">Stamping Polish</option>
                  <option value="Top Coat">Top Coat</option>
                  <option value="Treatment">Treatment</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Quantity
                </label>
                <input
                  type="number"
                  min="1"
                  value={quantity}
                  onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                  className="input-light font-body"
                />
              </div>
            </div>

            {/* Price + Currency + Storage Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-1">
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Purchase Price
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">{purchaseSymbol}</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    className={`input-light font-body ${purchaseSymbolPad}`}
                    placeholder="0.00"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-700 italic">
                  Defaults to your profile currency. Change per item if you paid in a different currency.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Currency
                </label>
                <CurrencyPicker
                  value={purchaseCurrency}
                  onChange={setPurchaseCurrency}
                  pinnedCurrencies={pinnedCurrencies}
                  onPinChange={setPinnedCurrencies}
                  sellerDefault={currency.code}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Storage Location
                </label>
                <input
                  type="text"
                  value={storageLocation}
                  onChange={(e) => setStorageLocation(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Helmer 1, Drawer 3"
                />
              </div>
            </div>

            {/* Purchase Date */}
            <div>
              <label className="block text-sm font-medium text-light-secondary mb-1">
                Purchase Date
              </label>
              <input
                type="date"
                value={purchaseDate}
                onChange={(e) => setPurchaseDate(e.target.value)}
                className="input-light font-body"
              />
            </div>

            {/* Swatch Number */}
            <div className="w-1/2">
              <label className="block text-sm font-medium text-light-secondary mb-1">
                Swatch Number
              </label>
              <input
                type="text"
                value={swatchNumber}
                onChange={(e) => setSwatchNumber(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., 1234"
              />
            </div>

            {/* Color Description */}
            <div>
              <label className="block text-sm font-medium text-light-secondary mb-1">
                Color Description
              </label>
              <textarea
                value={colorDescription}
                onChange={(e) => setColorDescription(e.target.value)}
                className="textarea-light font-body"
                rows={2}
                placeholder="e.g., Deep teal with scattered holographic sparkle and blue-to-purple shift"
              />
            </div>

            {/* Scent (optional, for scented polishes) */}
            <div>
              <label className="block text-sm font-medium text-light-secondary mb-1">
                Scent (if scented)
              </label>
              <input
                type="text"
                value={scent}
                onChange={(e) => setScent(e.target.value)}
                className="input-light font-body"
                placeholder="e.g., Vanilla, Rose, Cotton Candy"
              />
            </div>
          </div>

          {/* === RELEASE INFO - NEW CLEAN STRUCTURE === */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">
              📅 Release Info
            </h3>
            
            {/* Release Type Dropdown */}
            <div>
              <label className="block text-sm font-medium text-light-secondary mb-1">
                Release Type
              </label>
              <select
                value={releaseType}
                onChange={(e) => setReleaseType(e.target.value as ReleaseType)}
                className="select-light font-body"
              >
                {RELEASE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.emoji} {option.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Conditional Fields Based on Release Type */}
            {renderReleaseFields()}

            {/* Always Available: Year, Month & Season */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Year
                </label>
                <input
                  type="number"
                  value={releaseYear}
                  onChange={(e) => setReleaseYear(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., 2024"
                  min="2000"
                  max="2030"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Month
                </label>
                <select
                  value={releaseMonth}
                  onChange={(e) => setReleaseMonth(e.target.value)}
                  className="select-light font-body"
                >
                  <option value="">Unknown</option>
                  <option value="1">❄️ Jan</option>
                  <option value="2">❄️ Feb</option>
                  <option value="3">🌷 Mar</option>
                  <option value="4">🌷 Apr</option>
                  <option value="5">🌷 May</option>
                  <option value="6">☀️ Jun</option>
                  <option value="7">☀️ Jul</option>
                  <option value="8">☀️ Aug</option>
                  <option value="9">🍂 Sep</option>
                  <option value="10">🍂 Oct</option>
                  <option value="11">🍂 Nov</option>
                  <option value="12">❄️ Dec</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">
                  Season
                </label>
                <select
                  value={releaseSeason}
                  onChange={(e) => setReleaseSeason(e.target.value)}
                  className="select-light font-body"
                >
                  <option value="">Unknown</option>
                  <option value="Spring">🌷 Spring</option>
                  <option value="Summer">☀️ Summer</option>
                  <option value="Fall">🍂 Fall</option>
                  <option value="Winter">❄️ Winter</option>
                </select>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-1">Month takes priority over season if both are set</p>

            {/* Re-release Section */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRerelease}
                  onChange={(e) => setIsRerelease(e.target.checked)}
                  className="w-4 h-4 text-amber-600 rounded focus:ring-amber-500"
                />
                <span className="text-sm font-medium text-amber-700">
                  🔄 This is a re-release of an earlier polish
                </span>
              </label>
              
              {isRerelease && (
                <div className="grid grid-cols-2 gap-3 pt-2 border-t border-amber-200">
                  <div>
                    <label className="block text-sm font-medium text-amber-700 mb-1">
                      Original Year
                    </label>
                    <input
                      type="number"
                      value={originalReleaseYear}
                      onChange={(e) => setOriginalReleaseYear(e.target.value)}
                      className="input-light font-body"
                      placeholder="e.g., 2019"
                      min="2000"
                      max="2030"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-amber-700 mb-1">
                      Original Season
                    </label>
                    <select
                      value={originalReleaseSeason}
                      onChange={(e) => setOriginalReleaseSeason(e.target.value)}
                      className="select-light font-body"
                    >
                      <option value="">Select season...</option>
                      <option value="Spring">🌷 Spring</option>
                      <option value="Summer">☀️ Summer</option>
                      <option value="Fall">🍂 Fall</option>
                      <option value="Winter">❄️ Winter</option>
                    </select>
                  </div>
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-amber-700 mb-1">
                  📝 Release Notes <span className="font-normal text-amber-500">(optional)</span>
                </label>
                <textarea
                  value={releaseNotes}
                  onChange={(e) => setReleaseNotes(e.target.value)}
                  className="input-light font-body text-sm"
                  placeholder="e.g., New label art, slightly tweaked formula, limited restock..."
                  rows={2}
                />
              </div>
            </div>

            {/* Always Available: Collab */}
            <div className="p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-3">
              <div>
                <label className="block text-sm font-medium text-violet-700 mb-1">
                  🤝 Collab With <span className="text-xs text-violet-400">(can apply to any release type)</span>
                </label>
                <input
                  type="text"
                  value={collabWith}
                  onChange={(e) => setCollabWith(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., @gotnail, @varnishedvampire, Simply Nailogical"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-violet-700 mb-1">
                  🎨 Collab Theme <span className="font-normal text-violet-400">(optional)</span>
                </label>
                <input
                  type="text"
                  value={collabTheme}
                  onChange={(e) => setCollabTheme(e.target.value)}
                  className="input-light font-body"
                  placeholder="e.g., Halloween 2024, Anniversary Collection"
                />
              </div>
            </div>

            {/* Always Available: Duo/Trio/Set */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-1">
                    👯 Set Type <span className="text-xs text-amber-400">(if part of a set)</span>
                  </label>
                  <select
                    value={setType}
                    onChange={(e) => setSetType(e.target.value as typeof setType)}
                    className="select-light font-body"
                  >
                    <option value="">Not part of a set</option>
                    <option value="duo">Duo (2 polishes)</option>
                    <option value="trio">Trio (3 polishes)</option>
                    <option value="quad">Quad (4 polishes)</option>
                    <option value="set">Larger Set (5+)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-amber-700 mb-1">
                    🏷️ Set Name <span className="font-normal text-amber-400">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={setNameField}
                    onChange={(e) => setSetNameField(e.target.value)}
                    className="input-light font-body"
                    placeholder="e.g., Autumn Duo, Holiday Trio"
                    disabled={!setType}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* === NOTES === */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">
              📝 Notes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">Public Notes</label>
                <textarea
                  value={publicNotes}
                  onChange={(e) => setPublicNotes(e.target.value)}
                  rows={3}
                  className="textarea-light font-body"
                  placeholder="Visible to others viewing your collection..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-light-secondary mb-1">Private Notes 🔒</label>
                <textarea
                  value={privateNotes}
                  onChange={(e) => setPrivateNotes(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-yellow-200 bg-yellow-50 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent resize-none font-body"
                  placeholder="Only you can see these..."
                />
              </div>
            </div>
          </div>

          {/* === TOGGLES === */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">
              🎛️ Toggles
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <button
                type="button"
                onClick={() => setIsFavorite(!isFavorite)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-default font-body ${
                  isFavorite
                    ? 'border-red-300 bg-red-50 text-red-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Heart className={`w-4 h-4 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                <span className="text-sm">Favorite</span>
              </button>

              <button
                type="button"
                onClick={() => setIsRareLE(!isRareLE)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-default font-body ${
                  isRareLE
                    ? 'border-purple-300 bg-purple-50 text-purple-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <AppIcons.RareLE className={`w-4 h-4 ${isRareLE ? 'text-purple-500' : ''}`} />
                <span className="text-sm">Rare/LE</span>
              </button>

              <button
                type="button"
                onClick={() => setNeedsInfo(!needsInfo)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-default font-body ${
                  needsInfo
                    ? 'border-amber-300 bg-amber-50 text-amber-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Info className={`w-4 h-4 ${needsInfo ? 'text-amber-500' : ''}`} />
                <span className="text-sm">Needs Info</span>
              </button>

              <button
                type="button"
                onClick={() => setSwatched(!swatched)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-default font-body ${
                  swatched
                    ? 'border-teal-300 bg-teal-50 text-teal-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Eye className={`w-4 h-4 ${swatched ? 'text-teal-500' : ''}`} />
                <span className="text-sm">Swatched</span>
              </button>

              <button
                type="button"
                onClick={() => setIsPrivate(!isPrivate)}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-default font-body ${
                  isPrivate
                    ? 'border-slate-400 bg-slate-100 text-slate-700'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
                title="Hide from public profile, brand pages, community aggregations, and Discover"
              >
                <span className="text-sm">🔒 Private</span>
              </button>
            </div>
            {isPrivate && isForDestash && (
              <div className="mt-1 text-xs text-amber-600 italic font-body">
                This item is also listed for Destash — buyers can still see it on Marketplace.
              </div>
            )}

            {/* Wishlist Toggle with Priority */}
            <div className={`border rounded-lg p-3 transition-default ${onWishlist ? 'border-rose-300 bg-rose-50' : 'border-gray-200'}`}>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={onWishlist}
                  onChange={(e) => setOnWishlist(e.target.checked)}
                  className="w-5 h-5 rounded text-rose-500 focus:ring-rose-500"
                />
                <span className={`font-medium font-body ${onWishlist ? 'text-rose-700' : 'text-gray-600'}`}>
                  On Wishlist
                </span>
              </label>

              {onWishlist && (
                <div className="mt-3 flex gap-2">
                  {(['iso', 'diso', 'unicorn'] as const).map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => setIsoPriority(priority)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium transition-default ${
                        isoPriority === priority
                          ? `${PriorityColors[priority].bg} ${PriorityColors[priority].text}`
                          : 'bg-white border border-gray-300 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {priority === 'iso' && '🔍 ISO'}
                      {priority === 'diso' && '🔥 DISO'}
                      {priority === 'unicorn' && '🦄 Unicorn'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* === TAGS SECTION === */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">
              🏷️ Tags
            </h3>
            <TagSelector
              baseType={baseType}
              finish={finish}
              holoType={holoType}
              colorShift={colorShift}
              particles={particles}
              specialFeatures={specialFeatures}
              colorFamily={colorFamily}
              utility={utility}
              colorCharacter={colorCharacter}
              onChange={handleTagChange}
              defaultExpanded={[]}
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom tags
              </label>
              <div className="text-xs text-gray-500 mb-1">
                Freeform labels for anything not covered by the structured
                categories above — e.g. "gift", "wedding manicure",
                "spring 2025".
              </div>
              <CustomTagsEditor
                value={customTags}
                onChange={setCustomTags}
                placeholder="Add a tag and press Enter or comma"
              />
            </div>
          </div>

          {/* === LIST FOR DESTASH === */}
          <div className="space-y-4">
            <h3 className="section-header-light font-body">
              🏪 List for Destash
            </h3>

            <div className={`border rounded-lg p-4 transition-default ${isForDestash ? 'border-green-300 bg-green-50' : 'border-gray-300/60'}`}>
              {/* Main Toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isForDestash}
                  onChange={(e) => {
                    setIsForDestash(e.target.checked);
                    if (e.target.checked) {
                      // Set default quantity when enabling
                      setDestashQuantityAvailable(quantity || 1);
                    } else {
                      // Clear destash fields when turning off
                      setDestashCondition('');
                      setDestashPrice('');
                      setIsOpenToSwaps(false);
                      setDestashNotes('');
                      setDestashQuantityAvailable(quantity || 1);
                    }
                  }}
                  className="w-5 h-5 rounded text-green-500 focus:ring-green-500"
                />
                <Store className={`w-5 h-5 ${isForDestash ? 'text-green-600' : 'text-light-muted'}`} />
                <span className={`font-medium font-body ${isForDestash ? 'text-green-800' : 'text-light-secondary'}`}>
                  List on Marketplace
                </span>
              </label>

              {/* Expanded fields when listing is enabled */}
              {isForDestash && (
                <div className="mt-4 space-y-4 pt-4 border-t border-green-200">
                  {/* Quantity Available - Only show if quantity > 1 */}
                  {quantity > 1 && (
                    <div>
                      <label className="block text-sm font-bold text-light-secondary mb-2 font-body">
                        How many to list? <span className="text-xs text-light-muted font-normal">(You have {quantity})</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {Array.from({ length: quantity }, (_, i) => i + 1).map((num) => (
                          <button
                            key={num}
                            type="button"
                            onClick={() => setDestashQuantityAvailable(num)}
                            className={`w-10 h-10 rounded-lg font-bold transition-default ${
                              destashQuantityAvailable === num
                                ? 'bg-green-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                          >
                            {num}
                          </button>
                        ))}
                      </div>
                      {destashQuantityAvailable < quantity && (
                        <p className="mt-2 text-sm text-light-secondary">
                          ✨ Listing {destashQuantityAvailable}, keeping {quantity - destashQuantityAvailable}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Condition - Required */}
                  <ConditionSelector
                    itemType="polish"
                    value={destashCondition}
                    onChange={setDestashCondition}
                    required={true}
                  />

                  {/* Price - Required */}
                  <div>
                    <label className="block text-sm font-bold text-light-secondary mb-1 font-body">
                      Price <span className="text-red-500">*</span>
                    </label>
                    <div className="relative w-36">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-light-muted">{currency.symbol}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={destashPrice}
                        onChange={(e) => setDestashPrice(e.target.value)}
                        placeholder="0.00"
                        className={`w-full ${symbolPad} pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${!destashPrice ? 'border-amber-300' : 'input-light'}`}
                      />
                    </div>
                    {!destashPrice && (
                      <p className="mt-1 text-xs text-amber-600">Please enter a price</p>
                    )}
                    <p className="mt-1 text-xs text-gray-700 italic">
                      Shown to buyers in <strong>{currency.label}</strong>. Change your currency in <a href="/settings" className="underline hover:text-purple-600">Settings</a>.
                    </p>
                  </div>

                  {/* Open to Swaps - Optional */}
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isOpenToSwaps}
                      onChange={(e) => setIsOpenToSwaps(e.target.checked)}
                      className="w-5 h-5 rounded text-blue-500 focus:ring-blue-500"
                    />
                    <Repeat className="icon-sm text-blue-500" />
                    <span className="font-medium text-light-secondary font-body">Open to Swaps</span>
                    <span className="text-xs text-light-muted">(optional)</span>
                  </label>

                  {/* Notes - Optional */}
                  <div>
                    <label className="block text-sm font-bold text-light-secondary mb-1 font-body">
                      Destash Notes <span className="text-xs text-light-muted font-normal">(optional)</span>
                    </label>
                    <textarea
                      value={destashNotes}
                      onChange={(e) => setDestashNotes(e.target.value)}
                      placeholder="e.g., Only swatched once, beautiful color but doesn't suit my skin tone..."
                      rows={2}
                      className="textarea-light focus:ring-green-500"
                    />
                  </div>

                  {/* Warning message */}
                  <p className="text-xs text-light-muted flex items-center gap-1">
                    <span>⚠️</span>
                    This item will be visible to other users on the marketplace
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Progress */}
          {isSubmitting && uploadProgress > 0 && uploadProgress < 100 && (
            <div className="space-y-1">
              <div className="flex justify-between text-sm text-light-secondary">
                <span>Uploading photos...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className={`h-full ${CategoryColors.polish.bg} transition-slow rounded-full`}
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Photo rights attestation was moved from here to right after
              the Photos section so it reads paired with the upload,
              rather than as a legalese footnote before Submit. See
              <PhotoRightsAttestation> inside the Photos section above. */}

          {/* Buttons */}
          <div className="flex gap-3 pt-4 divider-light">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 btn-light-secondary font-body"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                isSubmitting ||
                !brand.trim() ||
                !name.trim() ||
                (isForDestash && (!destashCondition || !destashPrice)) ||
                (pendingPhotos.length > 0 && !photoRightsConfirmed)
              }
              className="flex-1 btn-light-primary disabled:opacity-50 disabled:cursor-not-allowed font-body"
            >
              {isSubmitting ? 'Adding...' : isForDestash ? '✓ Add & List on Marketplace' : '✓ Add Polish'}
            </button>
          </div>
        </form>
        </>
        )}
      </div>
      {/* Storage Upgrade Modal */}
      {showStorageUpgradeModal && (
        <StorageUpgradeModal
          isOpen={showStorageUpgradeModal}
          onClose={() => setShowStorageUpgradeModal(false)}
        />
      )}
    </div>
  );
}
